import React, { useState, useEffect } from 'react';
import statusService, { Status, CreateStatusRequest, UpdateStatusRequest } from '../services/statusService';
import './StatusManagement.css';

interface StatusManagementProps {
  onClose?: () => void;
}

const StatusManagement: React.FC<StatusManagementProps> = ({ onClose }) => {
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState<Status | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);

  // Формы
  const [createForm, setCreateForm] = useState<CreateStatusRequest>({
    name: '',
    description: '',
    color: '#3B82F6'
  });

  const [editForm, setEditForm] = useState<UpdateStatusRequest>({
    name: '',
    description: '',
    color: '#3B82F6'
  });

  useEffect(() => {
    fetchStatuses();
  }, []);

  const fetchStatuses = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await statusService.getStatuses();
      setStatuses(response.content);
    } catch (err) {
      console.error('Error fetching statuses:', err);
      setError('Ошибка при загрузке статусов');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStatus = async () => {
    try {
      if (!createForm.name.trim()) {
        alert('Название статуса обязательно');
        return;
      }

      await statusService.createStatus(createForm);
      setIsCreateModalOpen(false);
      setCreateForm({ name: '', description: '', color: '#3B82F6' });
      fetchStatuses();
      alert('Статус успешно создан!');
    } catch (err) {
      console.error('Error creating status:', err);
      alert('Ошибка при создании статуса');
    }
  };

  const handleUpdateStatus = async () => {
    try {
      if (!editingStatus) return;

      const updateData: UpdateStatusRequest = {};
      if (editForm.name) updateData.name = editForm.name;
      if (editForm.description !== undefined) updateData.description = editForm.description;
      if (editForm.color) updateData.color = editForm.color;

      await statusService.updateStatus(editingStatus.id, updateData);
      setEditingStatus(null);
      setEditForm({ name: '', description: '', color: '#3B82F6' });
      fetchStatuses();
      alert('Статус успешно обновлен!');
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Ошибка при обновлении статуса');
    }
  };

  const handleDeleteStatus = async (status: Status) => {
    if (!window.confirm(`Вы уверены, что хотите удалить статус "${status.name}"?`)) {
      return;
    }

    try {
      await statusService.deleteStatus(status.id);
      fetchStatuses();
      alert('Статус успешно удален!');
    } catch (err) {
      console.error('Error deleting status:', err);
      alert('Ошибка при удалении статуса');
    }
  };

  const handleCreateDefaultStatuses = async (type: 'request' | 'flight') => {
    try {
      const defaultStatuses = type === 'request' 
        ? statusService.getDefaultRequestStatuses()
        : statusService.getDefaultFlightStatuses();

      for (const statusData of defaultStatuses) {
        await statusService.createStatus(statusData);
      }

      fetchStatuses();
      alert(`Стандартные статусы для ${type === 'request' ? 'заявок' : 'рейсов'} успешно созданы!`);
    } catch (err) {
      console.error('Error creating default statuses:', err);
      alert('Ошибка при создании стандартных статусов');
    }
  };

  const filteredStatuses = statuses.filter(status => {
    const matchesSearch = status.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (status.description && status.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesActive = !showActiveOnly || status.isActive;
    return matchesSearch && matchesActive;
  });

  const openEditModal = (status: Status) => {
    setEditingStatus(status);
    setEditForm({
      name: status.name,
      description: status.description || '',
      color: status.color
    });
  };

  const closeEditModal = () => {
    setEditingStatus(null);
    setEditForm({ name: '', description: '', color: '#3B82F6' });
  };

  if (loading) {
    return (
      <div className="status-management">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Загрузка статусов...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="status-management">
      <div className="status-header">
        <h2>Управление статусами</h2>
        <div className="status-actions">
          <button 
            onClick={() => setIsCreateModalOpen(true)} 
            className="create-status-btn"
          >
            Создать статус
          </button>
          <button 
            onClick={() => handleCreateDefaultStatuses('request')} 
            className="create-default-btn"
          >
            Стандартные для заявок
          </button>
          <button 
            onClick={() => handleCreateDefaultStatuses('flight')} 
            className="create-default-btn"
          >
            Стандартные для рейсов
          </button>
        </div>
      </div>

      {error && (
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={fetchStatuses} className="retry-button">
            Попробовать снова
          </button>
        </div>
      )}

      <div className="status-filters">
        <input
          type="text"
          placeholder="Поиск по названию или описанию..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <label className="filter-checkbox">
          <input
            type="checkbox"
            checked={showActiveOnly}
            onChange={(e) => setShowActiveOnly(e.target.checked)}
          />
          Только активные
        </label>
      </div>

      <div className="statuses-list">
        {filteredStatuses.map((status) => (
          <div key={status.id} className={`status-card ${!status.isActive ? 'inactive' : ''}`}>
            <div className="status-info">
              <div className="status-header-info">
                <span 
                  className="status-color" 
                  style={{ backgroundColor: status.color }}
                ></span>
                <h3 className="status-name">{status.name}</h3>
                <span className={`status-active-badge ${status.isActive ? 'active' : 'inactive'}`}>
                  {status.isActive ? 'Активен' : 'Неактивен'}
                </span>
              </div>
              {status.description && (
                <p className="status-description">{status.description}</p>
              )}
              <div className="status-meta">
                <span>Создан: {new Date(status.createdAt).toLocaleDateString('ru-RU')}</span>
                <span>Обновлен: {new Date(status.updatedAt).toLocaleDateString('ru-RU')}</span>
              </div>
            </div>
            <div className="status-actions">
              <button 
                onClick={() => openEditModal(status)} 
                className="edit-btn"
              >
                Редактировать
              </button>
              <button 
                onClick={() => handleDeleteStatus(status)} 
                className="delete-btn"
              >
                Удалить
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredStatuses.length === 0 && !loading && (
        <div className="empty-state">
          <p>Статусы не найдены</p>
        </div>
      )}

      {/* Модальное окно создания */}
      {isCreateModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Создать новый статус</h3>
              <button onClick={() => setIsCreateModalOpen(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Название *</label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  placeholder="Введите название статуса"
                />
              </div>
              <div className="form-group">
                <label>Описание</label>
                <textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                  placeholder="Введите описание статуса"
                  rows={3}
                />
              </div>
              <div className="form-group">
                <label>Цвет</label>
                <input
                  type="color"
                  value={createForm.color}
                  onChange={(e) => setCreateForm({ ...createForm, color: e.target.value })}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setIsCreateModalOpen(false)} className="cancel-btn">
                Отмена
              </button>
              <button onClick={handleCreateStatus} className="save-btn">
                Создать
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно редактирования */}
      {editingStatus && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Редактировать статус</h3>
              <button onClick={closeEditModal} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Название</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder="Введите название статуса"
                />
              </div>
              <div className="form-group">
                <label>Описание</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  placeholder="Введите описание статуса"
                  rows={3}
                />
              </div>
              <div className="form-group">
                <label>Цвет</label>
                <input
                  type="color"
                  value={editForm.color}
                  onChange={(e) => setEditForm({ ...editForm, color: e.target.value })}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={closeEditModal} className="cancel-btn">
                Отмена
              </button>
              <button onClick={handleUpdateStatus} className="save-btn">
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatusManagement; 